import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { dominiAPI, clientsAPI } from '../lib/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Globe, Plus, Search, MoreHorizontal, Pencil, Trash2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle,
  Euro, RefreshCw,
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { differenceInDays, parseISO } from 'date-fns';

// ── Schema ────────────────────────────────────────────────────
const domainSchema = z.object({
  nome: z.string().min(2, 'Nome dominio richiesto'),
  registrar: z.string().optional(),
  dns_provider: z.string().optional(),
  nameservers: z.string().optional(),
  client_id: z.string().optional(),
  data_registrazione: z.string().optional(),
  data_scadenza: z.string().min(1, 'Data di scadenza richiesta'),
  auto_rinnovo: z.boolean().default(false),
  costo_annuo: z.coerce.number().min(0).default(0),
  ricavo_annuo: z.coerce.number().min(0).default(0),
  note: z.string().optional(),
});

// ── Helpers ───────────────────────────────────────────────────
const getStatusInfo = (stato, data_scadenza) => {
  const days = data_scadenza
    ? differenceInDays(new Date(data_scadenza), new Date())
    : null;

  switch (stato) {
    case 'attivo':
      return { label: 'Attivo', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
    case 'in_scadenza':
      return { label: `In scadenza (${days}gg)`, color: 'bg-amber-100 text-amber-700', icon: AlertTriangle };
    case 'scaduto':
      return { label: 'Scaduto', color: 'bg-red-100 text-red-700', icon: XCircle };
    case 'sospeso':
      return { label: 'Sospeso', color: 'bg-slate-100 text-slate-600', icon: XCircle };
    default:
      return { label: stato, color: 'bg-slate-100 text-slate-600', icon: Globe };
  }
};

// ── KPI Card ──────────────────────────────────────────────────
const KpiCard = ({ title, value, sub, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ── Main Page ─────────────────────────────────────────────────
export const Domini = () => {
  const [domains, setDomains] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { register, handleSubmit, reset, control, watch, formState: { errors } } = useForm({
    resolver: zodResolver(domainSchema),
    defaultValues: { auto_rinnovo: false, costo_annuo: 0, ricavo_annuo: 0 },
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [domsRes, clientsRes] = await Promise.all([
        dominiAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setDomains(domsRes.data);
      setClients(clientsRes.data);
    } catch {
      toast.error('Errore nel caricamento dei domini');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingDomain(null);
    reset({ auto_rinnovo: false, costo_annuo: 0, ricavo_annuo: 0 });
    setDialogOpen(true);
  };

  const openEdit = (domain) => {
    setEditingDomain(domain);
    reset({
      nome: domain.nome,
      registrar: domain.registrar || '',
      dns_provider: domain.dns_provider || '',
      nameservers: domain.nameservers || '',
      client_id: domain.client_id || '',
      data_registrazione: domain.data_registrazione
        ? domain.data_registrazione.split('T')[0] : '',
      data_scadenza: domain.data_scadenza
        ? domain.data_scadenza.split('T')[0] : '',
      auto_rinnovo: domain.auto_rinnovo ?? false,
      costo_annuo: domain.costo_annuo ?? 0,
      ricavo_annuo: domain.ricavo_annuo ?? 0,
      note: domain.note || '',
    });
    setDialogOpen(true);
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        data_scadenza: values.data_scadenza
          ? new Date(values.data_scadenza).toISOString() : null,
        data_registrazione: values.data_registrazione
          ? new Date(values.data_registrazione).toISOString() : null,
        client_id: values.client_id || null,
      };
      if (editingDomain) {
        await dominiAPI.update(editingDomain.id, payload);
        toast.success('Dominio aggiornato');
      } else {
        await dominiAPI.create(payload);
        toast.success('Dominio aggiunto');
      }
      setDialogOpen(false);
      loadAll();
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    try {
      await dominiAPI.delete(id);
      toast.success('Dominio eliminato');
      setDeleting(null);
      loadAll();
    } catch {
      toast.error('Errore nella eliminazione');
    }
  };

  // ── Filtraggio ─────────────────────────────────────────────
  const filtered = domains.filter((d) => {
    const matchSearch =
      d.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.registrar?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || d.stato === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── KPI ────────────────────────────────────────────────────
  const attivi = domains.filter((d) => d.stato === 'attivo').length;
  const inScadenza = domains.filter((d) => d.stato === 'in_scadenza').length;
  const scaduti = domains.filter((d) => d.stato === 'scaduto').length;
  const ricavoTotale = domains.reduce((s, d) => s + (d.ricavo_annuo || 0), 0);
  const costoTotale = domains.reduce((s, d) => s + (d.costo_annuo || 0), 0);
  const margine = ricavoTotale - costoTotale;

  const clientName = (id) => {
    const c = clients.find((c) => c.id === id);
    return c ? (c.azienda || c.nome) : '—';
  };

  return (
    <Layout title="Domini">
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <KpiCard title="Totale domini" value={domains.length} icon={Globe} color="bg-indigo-100 text-indigo-600" />
        <KpiCard title="Attivi" value={attivi} icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" />
        <KpiCard title="In scadenza" value={inScadenza} icon={AlertTriangle} color="bg-amber-100 text-amber-600" />
        <KpiCard title="Scaduti" value={scaduti} icon={XCircle} color="bg-red-100 text-red-600" />
        <KpiCard
          title="Ricavo annuo"
          value={formatCurrency(ricavoTotale)}
          sub={`Costo: ${formatCurrency(costoTotale)}`}
          icon={TrendingUp}
          color="bg-emerald-100 text-emerald-600"
        />
        <KpiCard
          title="Margine annuo"
          value={formatCurrency(margine)}
          sub={ricavoTotale > 0 ? `${Math.round((margine / ricavoTotale) * 100)}% del ricavo` : ''}
          icon={margine >= 0 ? TrendingUp : TrendingDown}
          color={margine >= 0 ? 'bg-indigo-100 text-indigo-600' : 'bg-red-100 text-red-600'}
        />
      </div>

      {/* Toolbar */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-1 w-full sm:max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Cerca dominio o registrar…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="attivo">Attivi</SelectItem>
                  <SelectItem value="in_scadenza">In scadenza</SelectItem>
                  <SelectItem value="scaduto">Scaduti</SelectItem>
                  <SelectItem value="sospeso">Sospesi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700 gap-2" onClick={openNew}>
              <Plus className="h-4 w-4" />
              Nuovo dominio
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Globe className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">Nessun dominio trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dominio</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Registrar</TableHead>
                    <TableHead>Scadenza</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Costo/anno</TableHead>
                    <TableHead className="text-right">Ricavo/anno</TableHead>
                    <TableHead className="text-right">Margine</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((domain) => {
                    const si = getStatusInfo(domain.stato, domain.data_scadenza);
                    const Icon = si.icon;
                    const margD = (domain.ricavo_annuo || 0) - (domain.costo_annuo || 0);
                    return (
                      <TableRow key={domain.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-slate-400 shrink-0" />
                            <span>{domain.nome}</span>
                            {domain.auto_rinnovo && (
                              <RefreshCw className="h-3 w-3 text-indigo-400" title="Rinnovo automatico" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {domain.client_id ? clientName(domain.client_id) : '—'}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {domain.registrar || '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {domain.data_scadenza ? formatDate(domain.data_scadenza) : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`gap-1 text-xs font-medium border-0 ${si.color}`}>
                            <Icon className="h-3 w-3" />
                            {si.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-600">
                          {formatCurrency(domain.costo_annuo || 0)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-600">
                          {formatCurrency(domain.ricavo_annuo || 0)}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          <span className={margD >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                            {formatCurrency(margD)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(domain)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Modifica
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => setDeleting(domain)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Add/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDomain ? 'Modifica dominio' : 'Nuovo dominio'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 mt-2">
            {/* Nome + Stato */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1">
                <Label>Nome dominio *</Label>
                <Input {...register('nome')} placeholder="esempio.it" />
                {errors.nome && <p className="text-xs text-red-500">{errors.nome.message}</p>}
              </div>
            </div>

            {/* Registrar + DNS */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Registrar</Label>
                <Input {...register('registrar')} placeholder="Aruba, GoDaddy…" />
              </div>
              <div className="space-y-1">
                <Label>DNS Provider</Label>
                <Input {...register('dns_provider')} placeholder="Cloudflare, Aruba…" />
              </div>
            </div>

            {/* Nameservers */}
            <div className="space-y-1">
              <Label>Nameserver</Label>
              <Input {...register('nameservers')} placeholder="ns1.example.com, ns2.example.com" />
            </div>

            {/* Cliente */}
            <div className="space-y-1">
              <Label>Cliente</Label>
              <Controller
                name="client_id"
                control={control}
                render={({ field }) => (
                  <Select value={field.value || 'none'} onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona cliente (opzionale)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun cliente</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.azienda || c.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Data registrazione</Label>
                <Input type="date" {...register('data_registrazione')} />
              </div>
              <div className="space-y-1">
                <Label>Data scadenza *</Label>
                <Input type="date" {...register('data_scadenza')} />
                {errors.data_scadenza && <p className="text-xs text-red-500">{errors.data_scadenza.message}</p>}
              </div>
            </div>

            {/* Costi e ricavi */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Costo annuo (€)</Label>
                <Input type="number" step="0.01" min="0" {...register('costo_annuo')} />
              </div>
              <div className="space-y-1">
                <Label>Ricavo annuo (€)</Label>
                <Input type="number" step="0.01" min="0" {...register('ricavo_annuo')} />
              </div>
            </div>

            {/* Auto rinnovo */}
            <div className="flex items-center gap-3">
              <Controller
                name="auto_rinnovo"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="auto_rinnovo"
                    checked={field.value}
                    onChange={field.onChange}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                  />
                )}
              />
              <Label htmlFor="auto_rinnovo" className="cursor-pointer font-normal">
                Rinnovo automatico attivo
              </Label>
            </div>

            {/* Note */}
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea {...register('note')} placeholder="Note aggiuntive…" rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                {editingDomain ? 'Salva modifiche' : 'Aggiungi dominio'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Elimina dominio</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mt-2">
            Sei sicuro di voler eliminare <strong>{deleting?.nome}</strong>? L'operazione è irreversibile.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleting(null)}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleting?.id)}
            >
              Elimina
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
